/**
 * Custom Facets Handler
 * Maneja el comportamiento de los filtros con checkboxes y botón de aplicar
 */

class CustomFacetsHandler {
  constructor() {
    this.form = null;
    this.checkboxes = [];
    this.applyButton = null;
    this.clearButton = null;
    this.priceSliders = [];
    
    this.init();
  }

  init() {
    // Esperar a que el DOM esté completamente cargado
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupEventListeners());
    } else {
      this.setupEventListeners();
    }
  }

  setupEventListeners() {
    // Buscar el formulario de filtros (con o sin data-filter-form)
    this.form = document.querySelector('#FacetFiltersForm');
    
    if (!this.form) {
      // Retry en caso de que el formulario no esté disponible aún
      setTimeout(() => this.setupEventListeners(), 500);
      return;
    }

    console.log('Custom Facets: Initializing...');
    
    // Encontrar elementos
    this.checkboxes = this.form.querySelectorAll('input[data-filter-checkbox]');
    this.applyButton = this.form.querySelector('[data-filter-apply]');
    this.clearButton = this.form.querySelector('[data-filter-clear]');
    
    console.log('Clear button found:', this.clearButton);
    
    // Configurar event listeners
    this.setupFormSubmission();
    this.setupClearButton();
    this.setupCheckboxListeners();
    
    // Prevenir el comportamiento por defecto de los enlaces
    this.preventDefaultLinks();
    
    // Interceptar el sistema de facets existente
    this.interceptExistingFacets();
    
    console.log(`Custom Facets: Setup complete with ${this.checkboxes.length} checkboxes`);
  }

  setupFormSubmission() {
    if (this.applyButton) {
      this.applyButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.applyFilters();
      });
    }

    // También manejar el submit del formulario
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.applyFilters();
    });
  }

  setupClearButton() {
    if (this.clearButton) {
      console.log('Setting up clear button');
      
      // Remover event listeners previos si existen
      const newClearButton = this.clearButton.cloneNode(true);
      this.clearButton.parentNode.replaceChild(newClearButton, this.clearButton);
      this.clearButton = newClearButton;
      
      this.clearButton.addEventListener('click', (e) => {
        console.log('Clear button clicked');
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.clearAllFilters();
        return false;
      }, true);
    } else {
      console.warn('Clear button not found');
      // Intentar buscar el botón de otra manera
      setTimeout(() => {
        this.clearButton = document.querySelector('[data-filter-clear]');
        if (this.clearButton) {
          console.log('Clear button found on retry');
          this.setupClearButton();
        }
      }, 1000);
    }
  }

  setupCheckboxListeners() {
    this.checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        this.updateCheckboxState(e.target);
      });
    });
  }

  preventDefaultLinks() {
    // Prevenir clics en enlaces de filtros existentes
    const filterLinks = this.form.querySelectorAll('a[href*="filter"]');
    filterLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
      });
    });
  }

  interceptExistingFacets() {
    // Interceptar el sistema de facets existente para prevenir navegación automática
    const existingFacetLinks = document.querySelectorAll('[data-filter-links] a, [data-sidebar-links] a');
    existingFacetLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        // Si el link está dentro de nuestro formulario personalizado, prevenir el comportamiento por defecto
        if (this.form && this.form.contains(link)) {
          e.preventDefault();
          e.stopPropagation();
        }
      });
    });
  }

  updateCheckboxState(checkbox) {
    const listItem = checkbox.closest('li');
    if (checkbox.checked) {
      listItem?.classList.add('is--selected');
    } else {
      listItem?.classList.remove('is--selected');
    }
  }

  applyFilters() {
    console.log('Custom Facets: Applying filters...');
    
    // Mostrar loading state
    this.showLoading();
    
    // Construir URL con filtros seleccionados
    const url = this.buildFilterURL();
    
    // Navegar a la URL con filtros
    window.location.href = url;
  }

  buildFilterURL() {
    const formData = new FormData(this.form);
    const params = new URLSearchParams(window.location.search);
    
    // Limpiar parámetros de filtros existentes
    for (const [key] of params.entries()) {
      if (key.includes('filter.') || key === 'sort_by') {
        params.delete(key);
      }
    }
    
    // Agregar nuevos filtros seleccionados
    // Para manejar múltiples valores del mismo filtro correctamente, 
    // necesitamos agregar cada uno como un parámetro separado
    for (const [name, value] of formData.entries()) {
      if (name !== 'q' && name !== 'type' && value) {
        // En lugar de concatenar con comas, usar append para crear múltiples parámetros
        params.append(name, value);
      }
    }
    
    // Construir URL final
    const baseURL = window.location.pathname;
    const queryString = params.toString();
    
    return queryString ? `${baseURL}?${queryString}` : baseURL;
  }

  clearAllFilters() {
    console.log('Custom Facets: Clearing all filters...');
    console.log('Current URL:', window.location.href);
    
    // Obtener la URL base (colección o búsqueda)
    const baseURL = window.location.pathname;
    console.log('Base URL:', baseURL);
    
    // Simplemente ir a la URL base sin ningún parámetro de filtro
    // Solo mantener sort_by si existe
    const currentParams = new URLSearchParams(window.location.search);
    const sortBy = currentParams.get('sort_by');
    
    let finalURL = baseURL;
    if (sortBy) {
      finalURL += '?sort_by=' + sortBy;
    }
    
    console.log('Final URL:', finalURL);
    console.log('Redirecting...');
    
    // Usar location.assign para asegurar que se ejecute
    window.location.assign(finalURL);
  }

  resetPriceSliders() {
    // Resetear sliders de precio si existe noUiSlider
    const priceSliders = this.form.querySelectorAll('.t4s-price_steps_slider');
    priceSliders.forEach(slider => {
      if (slider.noUiSlider) {
        const minInput = this.form.querySelector('.t4s-min_price');
        const maxInput = this.form.querySelector('.t4s-max_price');
        if (minInput && maxInput) {
          const min = parseFloat(minInput.dataset.min) || 0;
          const max = parseFloat(maxInput.dataset.max) || 1000;
          slider.noUiSlider.set([min, max]);
        }
      }
    });
  }

  showLoading() {
    if (this.applyButton) {
      this.applyButton.textContent = 'Aplicando...';
      this.applyButton.disabled = true;
    }
  }

  // Método para reinicializar después de AJAX
  reinitialize() {
    console.log('Custom Facets: Reinitializing...');
    this.setupEventListeners();
  }
}

// Inicializar el handler
let customFacetsHandler;

// Función global para reinicializar (útil para cuando se actualize el contenido via AJAX)
window.reinitializeCustomFacets = function() {
  if (customFacetsHandler) {
    customFacetsHandler.reinitialize();
  } else {
    customFacetsHandler = new CustomFacetsHandler();
  }
};

// Inicializar cuando el script se carga
customFacetsHandler = new CustomFacetsHandler();

// Hook para el sistema de facets existente
document.addEventListener('DOMContentLoaded', function() {
  
  // Event listener directo para el botón de limpiar como fallback
  document.addEventListener('click', function(e) {
    if (e.target.matches('[data-filter-clear]') || e.target.closest('[data-filter-clear]')) {
      console.log('Clear button clicked (direct listener)');
      e.preventDefault();
      e.stopPropagation();
      
      // Limpiar filtros directamente
      const baseURL = window.location.pathname;
      const currentParams = new URLSearchParams(window.location.search);
      const sortBy = currentParams.get('sort_by');
      
      let finalURL = baseURL;
      if (sortBy) {
        finalURL += '?sort_by=' + sortBy;
      }
      
      console.log('Redirecting to:', finalURL);
      window.location.assign(finalURL);
      return false;
    }
  }, true);
  
  // Escuchar cambios en el DOM para reinicializar cuando sea necesario
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        // Verificar si se agregó un nuevo formulario de filtros
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1 && (node.querySelector && node.querySelector('#FacetFiltersForm'))) {
            setTimeout(() => window.reinitializeCustomFacets(), 100);
          }
        });
      }
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
});
